import type { PreOrderSource, SourceEmail, SourceFile } from './sourcePreviewApi';

/**
 * Reguły podglądu źródła (S-29.10-01) — bez Reacta, żeby testy sprawdzały je
 * wprost: zakładki, bezpieczna ramka treści maila i to, którą zakładkę
 * otwiera „pokaż w źródle” przy polu.
 */

export type SourceTab =
  | { key: string; type: 'mail'; label: string; email: SourceEmail }
  | {
      key: string;
      type: 'file';
      label: string;
      file: SourceFile;
      /** Załącznik maila (`emailId`) albo plik od klienta przy prezleceniu. */
      origin: { emailId: number } | 'client';
    };

export const mailTabKey = (emailId: number) => `mail-${emailId}`;
export const emailFileTabKey = (emailId: number, fileId: number) => `mail-${emailId}-file-${fileId}`;
export const clientFileTabKey = (fileId: number) => `file-${fileId}`;

/**
 * Zakładki w kolejności z makiety: „Mail” i jego załączniki, potem kolejne
 * maile (zmiana, duplikat) z załącznikami, na końcu pliki od klienta.
 */
export function sourceTabs(source: PreOrderSource | null): SourceTab[] {
  if (!source) return [];
  const tabs: SourceTab[] = [];
  source.emails.forEach((email, i) => {
    tabs.push({
      key: mailTabKey(email.id),
      type: 'mail',
      label: i === 0 && email.role === 'SOURCE' ? 'Mail' : `Mail: ${email.roleLabel}`,
      email,
    });
    for (const file of email.attachments) {
      tabs.push({
        key: emailFileTabKey(email.id, file.id),
        type: 'file',
        label: file.filename,
        file,
        origin: { emailId: email.id },
      });
    }
  });
  for (const file of source.files) {
    tabs.push({
      key: clientFileTabKey(file.id),
      type: 'file',
      label: file.filename,
      file,
      origin: 'client',
    });
  }
  return tabs;
}

/**
 * Polityka treści ramki maila: nic z sieci (obrazy śledzące, czcionki,
 * arkusze stylów z zewnątrz), obrazy tylko osadzone w treści. Skryptów nie
 * wykona i tak — ramka ma `sandbox` bez `allow-scripts`.
 */
export const MAIL_FRAME_CSP = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:";

/**
 * Wartość atrybutu `sandbox` ramki maila — pusta: bez skryptów, formularzy,
 * okien, nawigacji rodzica i z własnym, obcym pochodzeniem (nie sięga do
 * aplikacji ani tokenu). Żadnego `allow-*`.
 */
export const MAIL_FRAME_SANDBOX = '';

export function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Dokument do ramki z treścią maila: polityka treści na samym początku
 * (przeglądarka stosuje ją, zanim przeczyta treść), potem HTML maila bez
 * zmian albo tekst jako tekst. Treść maila nigdy nie trafia do drzewa
 * aplikacji — tylko do `srcdoc` zamkniętej ramki.
 */
export function mailDocument(html: string | null, text: string | null): string {
  const head = `<meta http-equiv="Content-Security-Policy" content="${MAIL_FRAME_CSP}"><meta charset="utf-8">`;
  const body =
    html && html.trim() !== ''
      ? html
      : text && text.trim() !== ''
        ? `<pre style="font-family:system-ui,sans-serif;white-space:pre-wrap;margin:0">${escapeHtml(text)}</pre>`
        : '<p style="font-family:system-ui,sans-serif;color:#666">Wiadomość bez treści.</p>';
  return `${head}${body}`;
}

/** Pewność odczytu poniżej tej wartości — pole do sprawdzenia w źródle (ten sam próg co kolor „nie zielony”). */
export const LOW_CONFIDENCE = 0.8;

/**
 * Wskazanie źródła pola z odczytu maila (`extractedFields.fields[k].source`)
 * albo z poprawki człowieka („plik od klienta”): nazwa pliku, identyfikator
 * pliku albo maila. Kształt ustali tor maili — czytamy, co się da, a czego
 * nie rozpoznamy, to pierwszy mail (albo pierwsza zakładka).
 */
export type SourceHint =
  | string
  | {
      attachmentId?: unknown;
      fileId?: unknown;
      filename?: unknown;
      emailId?: unknown;
    }
  | null
  | undefined;

export function tabForHint(hint: SourceHint, tabs: readonly SourceTab[]): string | null {
  if (tabs.length === 0) return null;
  const fallback = (tabs.find((t) => t.type === 'mail') ?? tabs[0]).key;
  if (hint == null) return fallback;
  const byName = (name: string) =>
    tabs.find((t) => t.type === 'file' && t.file.filename.toLowerCase() === name.trim().toLowerCase())?.key;
  if (typeof hint === 'string') return byName(hint) ?? fallback;
  const id =
    typeof hint.attachmentId === 'number' ? hint.attachmentId : typeof hint.fileId === 'number' ? hint.fileId : null;
  if (id !== null) {
    // Załącznik maila ma sens tylko z mailem (identyfikatory dwóch tabel się powtarzają);
    // sam identyfikator to plik od klienta (źródło ustalenia „plik od klienta”).
    const key = typeof hint.emailId === 'number' ? emailFileTabKey(hint.emailId, id) : clientFileTabKey(id);
    if (tabs.some((t) => t.key === key)) return key;
  }
  if (typeof hint.filename === 'string') {
    const hit = byName(hint.filename);
    if (hit) return hit;
  }
  if (typeof hint.emailId === 'number') {
    const hit = tabs.find((t) => t.type === 'mail' && t.email.id === hint.emailId);
    if (hit) return hit.key;
  }
  return fallback;
}

/** Zapamiętany wybór „źródło otwarte” — w przeglądarce, dla wygody (bez magazynu działa domyślnie zamknięte). */
const OPEN_KEY = 'marathon.sourcePreview.open';

export function readOpenPreference(): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Podgląd dokumentu przy prezleceniu z `preOrderDocuments` (decyzja Bartka
 * 03.11.2026): dokument stoi obok danych od razu, a „Zwiń podgląd” zapamiętuje
 * wybór **osobno dla każdego konta** (klucz z numerem konta). Brak wpisu =
 * rozwinięty. Bez konta (np. w testach) — klucz wspólny.
 */
export const DOCUMENT_OPEN_KEY = 'marathon.preOrderDocuments.open';

export function documentOpenKey(userId: number | string | null | undefined): string {
  return userId === null || userId === undefined || userId === '' ? DOCUMENT_OPEN_KEY : `${DOCUMENT_OPEN_KEY}.${userId}`;
}

export function readDocumentOpenPreference(userId: number | string | null | undefined): boolean {
  try {
    return window.localStorage.getItem(documentOpenKey(userId)) !== '0';
  } catch {
    return true;
  }
}

export function writeDocumentOpenPreference(userId: number | string | null | undefined, open: boolean): void {
  try {
    window.localStorage.setItem(documentOpenKey(userId), open ? '1' : '0');
  } catch {
    // bez magazynu — wybór do odświeżenia strony
  }
}

export function writeOpenPreference(open: boolean): void {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    // bez magazynu — wybór do odświeżenia strony
  }
}

/**
 * Pola karty prezlecenia → klucze odczytu z maila (`extractedFields.fields`,
 * słownik `email/fieldDictionary.ts`) — żeby „pokaż w źródle” przy polu
 * otwierało miejsce, z którego odczyt pochodzi.
 */
export const RECORD_TO_EXTRACTED: Readonly<Record<string, string>> = {
  clientName: 'klient',
  loadingCity: 'adres_zaladunku',
  loadingDate: 'data_zaladunku',
  unloadingCity: 'adres_rozladunku',
  unloadingDate: 'data_rozladunku',
  cargo: 'towar',
  weight: 'waga',
  price: 'cena',
  pallets: 'palety',
};

interface ExtractedEntry {
  value?: unknown;
  confidence?: unknown;
  source?: unknown;
}

function extractedEntry(extracted: unknown, key: string): ExtractedEntry | null {
  const fields = (extracted as { fields?: unknown } | null | undefined)?.fields;
  if (!fields || typeof fields !== 'object') return null;
  const e = (fields as Record<string, unknown>)[key];
  return e && typeof e === 'object' ? (e as ExtractedEntry) : null;
}

const isEmpty = (v: unknown) => v == null || (typeof v === 'string' && v.trim() === '');

/** Pewność odczytu poniżej progu (brak pewności = nie oceniamy). */
export function lowConfidence(entry: { confidence?: unknown } | null | undefined): boolean {
  return typeof entry?.confidence === 'number' && entry.confidence < LOW_CONFIDENCE;
}

/** Wskazanie z odczytu: pole `source` przy odczycie, gdy jest napisem albo obiektem. */
export function extractedHint(entry: { source?: unknown } | null | undefined): SourceHint {
  const s = entry?.source;
  return typeof s === 'string' || (s && typeof s === 'object') ? (s as SourceHint) : null;
}

/**
 * Czy przy wierszu karty stoi „pokaż w źródle” i dokąd prowadzi:
 * 1. wartość ustalona z pliku od klienta (źródło ustalenia) — ten plik;
 * 2. odczyt z maila z niską pewnością — miejsce odczytu;
 * 3. pole bez wartości — miejsce odczytu, jeśli jest, inaczej mail.
 * Pole z wartością i bez wątpliwości — bez odnośnika.
 */
export function sourceLinkFor(
  record: Record<string, unknown>,
  keys: readonly string[],
  fieldSources?: Record<string, { attachment?: { id: number; deleted: boolean } | null } | undefined>,
): { show: boolean; hint: SourceHint } {
  for (const k of keys) {
    const a = fieldSources?.[k]?.attachment;
    if (a && !a.deleted) return { show: true, hint: { attachmentId: a.id } };
  }
  for (const k of keys) {
    const entry = RECORD_TO_EXTRACTED[k] ? extractedEntry(record.extractedFields, RECORD_TO_EXTRACTED[k]) : null;
    if (lowConfidence(entry)) return { show: true, hint: extractedHint(entry) };
  }
  for (const k of keys) {
    if (isEmpty(record[k])) {
      const entry = RECORD_TO_EXTRACTED[k] ? extractedEntry(record.extractedFields, RECORD_TO_EXTRACTED[k]) : null;
      return { show: true, hint: extractedHint(entry) };
    }
  }
  return { show: false, hint: null };
}
